import React, { useState, useEffect } from 'react';
import { getGalleryImages } from '../services/api';
import {
  adminCreateGalleryImage,
  adminUpdateGalleryImage,
  adminDeleteGalleryImage,
  adminReorderGalleryImages,
  adminUploadFile,
  adminBulkDeleteGalleryImages,
} from '../services/adminApi';
import { X, Plus, Trash2, GripVertical, Upload, ImageOff, Save, RotateCcw, UploadCloud, CheckSquare, Square } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CATEGORY_OPTIONS = [
  { id: 'events', name: 'Events' },
  { id: 'portraits', name: 'Self Portraits' },
  { id: 'landscapes', name: 'Landscapes & Nature' },
  { id: 'commercial', name: 'Commercial & Product' },
  { id: 'fineart', name: 'Fine Art' },
];

const emptyForm = () => ({
  category: [],
  title: '',
  description: '',
  image_url: '',
  thumbnail_url: '',
  order: 0,
});

// Sortable item used in reorder mode
const SortableGalleryItem = ({ image }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="admin-gallery-item admin-gallery-item-sortable"
      data-testid={`sortable-item-${image.id}`}
    >
      <div className="admin-drag-handle" {...attributes} {...listeners} data-testid={`drag-handle-${image.id}`}>
        <GripVertical size={20} />
      </div>
      <img src={image.thumbnail_url} alt={image.title || 'Gallery image'} className="admin-gallery-image" />
      <div className="admin-gallery-info">
        <h3 className="admin-gallery-title">{image.title || '(No title)'}</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {(Array.isArray(image.category) ? image.category : [image.category]).map((cat) => (
            <span key={cat} className="admin-gallery-category">{cat}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formDataList, setFormDataList] = useState([emptyForm()]);
  const [submitting, setSubmitting] = useState(false);

  // Reorder mode state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  // Compression preference (per-modal-session)
  const [compressEnabled, setCompressEnabled] = useState(true);
  const [compressingIndex, setCompressingIndex] = useState(null);

  // Bulk drag-and-drop upload state
  const [isDragOver, setIsDragOver] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total} | null

  // Bulk-select mode for deletion
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const data = await getGalleryImages();
      setImages(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load images');
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingImage(null);
    setFormDataList([emptyForm()]);
    setShowModal(true);
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormDataList([{
      category: Array.isArray(image.category) ? image.category : [image.category],
      title: image.title || '',
      description: image.description || '',
      image_url: image.image_url || '',
      thumbnail_url: image.thumbnail_url || '',
      order: image.order || 0,
    }]);
    setShowModal(true);
  };

  const handleDelete = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await adminDeleteGalleryImage(imageId);
        loadImages();
      } catch (err) {
        alert('Failed to delete image');
      }
    }
  };

  const handleAddImageSlot = () => {
    setFormDataList([...formDataList, emptyForm()]);
  };

  const handleRemoveImageSlot = (index) => {
    if (formDataList.length > 1) {
      setFormDataList(formDataList.filter((_, i) => i !== index));
    }
  };

  const updateFormData = (index, field, value) => {
    const newList = [...formDataList];
    newList[index] = { ...newList[index], [field]: value };
    setFormDataList(newList);
  };

  const handleCategoryToggle = (index, categoryId) => {
    const newList = [...formDataList];
    const categories = [...newList[index].category];
    const catIndex = categories.indexOf(categoryId);
    if (catIndex > -1) categories.splice(catIndex, 1);
    else categories.push(categoryId);
    newList[index] = { ...newList[index], category: categories };
    setFormDataList(newList);
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setCompressingIndex(index);
    try {
      let fullFile = file;
      if (compressEnabled) {
        fullFile = await imageCompression(file, {
          maxSizeMB: 1.5,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg',
        });
      }
      const thumbFile = await imageCompression(file, {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 600,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });

      // Upload both to object storage via backend
      const [fullResult, thumbResult] = await Promise.all([
        adminUploadFile(fullFile),
        adminUploadFile(thumbFile),
      ]);

      const newList = [...formDataList];
      newList[index] = {
        ...newList[index],
        image_url: fullResult.full_url,
        thumbnail_url: thumbResult.full_url,
      };
      setFormDataList(newList);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Failed to upload image. Please try a different file or check your connection.');
    } finally {
      setCompressingIndex(null);
    }
  };

  const clearImageData = (index) => {
    const newList = [...formDataList];
    newList[index] = { ...newList[index], image_url: '', thumbnail_url: '' };
    setFormDataList(newList);
  };

  // Process one file: compress (if enabled) + upload main + thumb. Returns the populated slot.
  const processOneFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      throw new Error(`${file.name} is not an image`);
    }
    let fullFile = file;
    if (compressEnabled) {
      fullFile = await imageCompression(file, {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
    }
    const thumbFile = await imageCompression(file, {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 600,
      useWebWorker: true,
      fileType: 'image/jpeg',
    });
    const [fullResult, thumbResult] = await Promise.all([
      adminUploadFile(fullFile),
      adminUploadFile(thumbFile),
    ]);
    return {
      ...emptyForm(),
      image_url: fullResult.full_url,
      thumbnail_url: thumbResult.full_url,
    };
  };

  // Bulk drop: process multiple files in parallel, each becomes its own form slot.
  const handleBulkFiles = async (fileList) => {
    if (editingImage) return; // edit mode is single-image only
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      alert('Please drop image files only (JPG, PNG, WebP, GIF).');
      return;
    }

    setBulkProgress({ done: 0, total: files.length });
    let doneCount = 0;
    const incrementDone = () => {
      doneCount += 1;
      setBulkProgress({ done: doneCount, total: files.length });
    };

    // Upload all in parallel with progress tracking
    const results = await Promise.allSettled(
      files.map((f) =>
        processOneFile(f).then((slot) => {
          incrementDone();
          return slot;
        })
      )
    );

    const successSlots = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);
    const failureCount = results.filter((r) => r.status === 'rejected').length;

    // Replace empty/blank first slot if user hasn't touched it; otherwise append.
    setFormDataList((prev) => {
      const firstIsEmpty =
        prev.length === 1 &&
        !prev[0].image_url &&
        !prev[0].description &&
        prev[0].category.length === 0 &&
        !prev[0].title;
      return firstIsEmpty ? successSlots : [...prev, ...successSlots];
    });

    setBulkProgress(null);
    if (failureCount > 0) {
      alert(`${successSlots.length} uploaded, ${failureCount} failed. Check console for details.`);
    }
  };

  // HTML5 drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when actually leaving the drop zone (not when entering children)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleBulkFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let i = 0; i < formDataList.length; i++) {
      const formData = formDataList[i];
      if (formData.category.length === 0) {
        alert(`Image ${i + 1}: Please select at least one category`);
        return;
      }
      if (!formData.description.trim()) {
        alert(`Image ${i + 1}: Description is required`);
        return;
      }
      if (!formData.image_url.trim()) {
        alert(`Image ${i + 1}: Image is required (upload a file or paste a URL)`);
        return;
      }
      if (!formData.thumbnail_url.trim()) {
        alert(`Image ${i + 1}: Thumbnail is required (upload a file or paste a URL)`);
        return;
      }
    }

    setSubmitting(true);

    try {
      if (editingImage) {
        await adminUpdateGalleryImage(editingImage.id, formDataList[0]);
        alert('Image updated successfully!');
      } else {
        let successCount = 0;
        let failCount = 0;

        for (const formData of formDataList) {
          try {
            await adminCreateGalleryImage(formData);
            successCount++;
          } catch (err) {
            failCount++;
            console.error('Failed to create image:', err);
          }
        }

        if (failCount === 0) {
          alert(`Successfully added ${successCount} image${successCount > 1 ? 's' : ''}!`);
        } else {
          alert(`Added ${successCount} image${successCount > 1 ? 's' : ''}. Failed to add ${failCount}.`);
        }
      }

      setShowModal(false);
      loadImages();
    } catch (err) {
      alert('Failed to save images');
    } finally {
      setSubmitting(false);
    }
  };

  // -- Reorder mode handlers --
  const enterReorderMode = () => {
    setReorderItems([...images]);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setReorderItems([]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setReorderItems((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const saveReorder = async () => {
    setSavingOrder(true);
    try {
      const payload = reorderItems.map((img, idx) => ({ id: img.id, order: idx + 1 }));
      await adminReorderGalleryImages(payload);
      await loadImages();
      setReorderMode(false);
      setReorderItems([]);
    } catch (err) {
      alert('Failed to save new order');
    } finally {
      setSavingOrder(false);
    }
  };

  // -- Selection / bulk delete handlers --
  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === images.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(images.map((i) => i.id)));
    }
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmMsg =
      selectedIds.size === images.length
        ? `Delete ALL ${selectedIds.size} images? This cannot be undone.`
        : `Delete ${selectedIds.size} selected image${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setBulkDeleting(true);
    try {
      const result = await adminBulkDeleteGalleryImages(Array.from(selectedIds));
      await loadImages();
      setSelectedIds(new Set());
      setSelectMode(false);
      alert(`Deleted ${result.deleted} image${result.deleted === 1 ? '' : 's'}.`);
    } catch (err) {
      alert('Bulk delete failed. Please try again.');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) return <div data-testid="gallery-loading">Loading...</div>;

  // ---- Reorder mode UI ----
  if (reorderMode) {
    return (
      <div data-testid="admin-gallery-reorder">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="admin-section-title">Reorder Gallery — drag to rearrange</h2>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="admin-button admin-button-secondary" onClick={cancelReorder} data-testid="cancel-reorder-btn">
              <RotateCcw size={16} style={{ marginRight: 6, verticalAlign: -3 }} />Cancel
            </button>
            <button className="admin-button" onClick={saveReorder} disabled={savingOrder} data-testid="save-reorder-btn">
              <Save size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
              {savingOrder ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </div>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Items at the top appear first in the public gallery.
        </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={reorderItems.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="admin-gallery-grid">
              {reorderItems.map((image) => (
                <SortableGalleryItem key={image.id} image={image} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    );
  }

  // ---- Normal grid UI ----
  const allSelected = images.length > 0 && selectedIds.size === images.length;
  return (
    <div data-testid="admin-gallery">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 className="admin-section-title">Gallery Management</h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {!selectMode && (
            <>
              <button
                className="admin-button admin-button-secondary"
                onClick={enterSelectMode}
                disabled={images.length === 0}
                data-testid="select-mode-btn"
              >
                <CheckSquare size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
                Select
              </button>
              <button
                className="admin-button admin-button-secondary"
                onClick={enterReorderMode}
                disabled={images.length < 2}
                data-testid="reorder-mode-btn"
              >
                <GripVertical size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
                Reorder
              </button>
              <button className="admin-button" onClick={handleAddNew} data-testid="add-new-image-btn">
                Add New Image(s)
              </button>
            </>
          )}
          {selectMode && (
            <button
              className="admin-button admin-button-secondary"
              onClick={exitSelectMode}
              disabled={bulkDeleting}
              data-testid="cancel-select-btn"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {selectMode && (
        <div className="admin-select-toolbar" data-testid="select-toolbar">
          <label className="admin-select-all" data-testid="select-all-toggle">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              data-testid="select-all-checkbox"
            />
            <span>{allSelected ? 'Deselect all' : 'Select all'} ({images.length})</span>
          </label>
          <div className="admin-select-toolbar-info" data-testid="selection-count">
            {selectedIds.size} selected
          </div>
          <button
            className="admin-button admin-button-danger"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0 || bulkDeleting}
            data-testid="bulk-delete-btn"
          >
            <Trash2 size={16} style={{ marginRight: 6, verticalAlign: -3 }} />
            {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
          </button>
        </div>
      )}

      <div className="admin-gallery-grid">
        {images.map((image) => {
          const isSelected = selectedIds.has(image.id);
          const cardClass = [
            'admin-gallery-item',
            selectMode ? 'admin-gallery-item-selectable' : '',
            isSelected ? 'is-selected' : '',
          ].filter(Boolean).join(' ');
          return (
            <div
              key={image.id}
              className={cardClass}
              data-testid={`gallery-item-${image.id}`}
              onClick={selectMode ? () => toggleSelected(image.id) : undefined}
            >
              {selectMode && (
                <div
                  className="admin-gallery-checkbox-overlay"
                  data-testid={`select-${image.id}`}
                  aria-label={isSelected ? 'Selected' : 'Not selected'}
                >
                  {isSelected ? <CheckSquare size={22} /> : <Square size={22} />}
                </div>
              )}
              <img src={image.thumbnail_url} alt={image.title || 'Gallery image'} className="admin-gallery-image" />
              <div className="admin-gallery-info">
                <h3 className="admin-gallery-title">{image.title || '(No title)'}</h3>
                <p className="admin-gallery-desc">{image.description}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {(Array.isArray(image.category) ? image.category : [image.category]).map((cat) => (
                    <span key={cat} className="admin-gallery-category">{cat}</span>
                  ))}
                </div>
                {!selectMode && (
                  <div className="admin-gallery-actions">
                    <button
                      className="admin-button admin-button-secondary"
                      onClick={() => handleEdit(image)}
                      data-testid={`edit-image-${image.id}`}
                    >
                      Edit
                    </button>
                    <button
                      className="admin-button admin-button-danger"
                      onClick={() => handleDelete(image.id)}
                      data-testid={`delete-image-${image.id}`}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)} data-testid="add-image-modal">
          <div className="admin-modal admin-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {editingImage ? 'Edit Image' : `Add New Images (${formDataList.length})`}
              </h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)} data-testid="close-modal-btn">
                <X size={24} />
              </button>
            </div>

            {/* Compression option */}
            <label className="admin-compress-toggle" data-testid="compress-toggle">
              <input
                type="checkbox"
                checked={compressEnabled}
                onChange={(e) => setCompressEnabled(e.target.checked)}
                data-testid="compress-checkbox"
              />
              <div>
                <strong>Compress images before upload</strong>
                <span style={{ display: 'block', color: '#666', fontSize: '0.85rem' }}>
                  Recommended. Resizes large photos (up to 1920px / 1.5MB) for faster page loads. Uncheck to upload originals.
                </span>
              </div>
            </label>

            {/* Bulk drag-and-drop zone — only available when adding new images */}
            {!editingImage && (
              <div
                className={`admin-bulk-dropzone ${isDragOver ? 'is-dragover' : ''} ${bulkProgress ? 'is-uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="bulk-dropzone"
              >
                <UploadCloud size={40} className="admin-bulk-dropzone-icon" />
                <div className="admin-bulk-dropzone-title">
                  {bulkProgress
                    ? `Uploading ${bulkProgress.done} of ${bulkProgress.total}…`
                    : isDragOver
                    ? 'Drop to upload'
                    : 'Drag & drop images here'}
                </div>
                <div className="admin-bulk-dropzone-subtitle">
                  or
                  <label className="admin-bulk-browse-link" data-testid="bulk-browse-btn">
                    browse to select multiple files
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleBulkFiles(e.target.files);
                        e.target.value = ''; // allow re-selecting the same files
                      }}
                      disabled={bulkProgress !== null}
                      data-testid="bulk-file-input"
                    />
                  </label>
                </div>
                {bulkProgress && (
                  <div className="admin-bulk-progress" data-testid="bulk-progress-bar">
                    <div
                      className="admin-bulk-progress-fill"
                      style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }}
                    />
                  </div>
                )}
                <div className="admin-bulk-dropzone-hint">
                  Each file becomes its own image card below — fill in categories &amp; description, then save.
                </div>
              </div>
            )}

            <form className="admin-form" onSubmit={handleSubmit}>
              <div className="admin-form-scroll-container">
                {formDataList.map((formData, index) => (
                  <div key={index} className="admin-image-form-card" data-testid={`image-form-${index}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '600' }}>Image {index + 1}</h3>
                      {!editingImage && formDataList.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveImageSlot(index)}
                          className="admin-button admin-button-danger"
                          style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                          data-testid={`remove-image-${index}`}
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      )}
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Categories (select one or more)</label>
                      <div className="admin-category-checkboxes">
                        {CATEGORY_OPTIONS.map((cat) => (
                          <label key={cat.id} className="admin-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.category.includes(cat.id)}
                              onChange={() => handleCategoryToggle(index, cat.id)}
                              data-testid={`category-${cat.id}-${index}`}
                            />
                            <span>{cat.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Title (optional)</label>
                      <input
                        type="text"
                        className="admin-form-input"
                        value={formData.title}
                        onChange={(e) => updateFormData(index, 'title', e.target.value)}
                        placeholder="Enter image title (optional)"
                        data-testid={`title-input-${index}`}
                      />
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Description</label>
                      <textarea
                        className="admin-form-textarea"
                        value={formData.description}
                        onChange={(e) => updateFormData(index, 'description', e.target.value)}
                        required
                        data-testid={`description-input-${index}`}
                      />
                    </div>

                    {/* Upload + preview block */}
                    <div className="admin-form-group">
                      <label className="admin-form-label">Image source</label>
                      <div className="admin-upload-row">
                        <label className="admin-upload-btn" data-testid={`upload-btn-${index}`}>
                          <Upload size={16} />
                          {compressingIndex === index ? 'Processing...' : 'Upload from device'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileUpload(index, e.target.files?.[0])}
                            data-testid={`file-input-${index}`}
                          />
                        </label>
                        {formData.image_url && (
                          <button
                            type="button"
                            onClick={() => clearImageData(index)}
                            className="admin-button admin-button-secondary"
                            style={{ padding: '0.5rem 1rem' }}
                            data-testid={`clear-image-${index}`}
                          >
                            <ImageOff size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Clear
                          </button>
                        )}
                      </div>

                      {formData.thumbnail_url && (
                        <div className="admin-upload-preview">
                          <img src={formData.thumbnail_url} alt="preview" />
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>Preview</span>
                        </div>
                      )}
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">…or paste a Full Image URL</label>
                      <input
                        type="text"
                        className="admin-form-input"
                        value={formData.image_url}
                        onChange={(e) => updateFormData(index, 'image_url', e.target.value)}
                        placeholder="https://images.unsplash.com/photo-xxxxx?w=800&q=80"
                        data-testid={`image-url-input-${index}`}
                      />
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Thumbnail URL</label>
                      <input
                        type="text"
                        className="admin-form-input"
                        value={formData.thumbnail_url}
                        onChange={(e) => updateFormData(index, 'thumbnail_url', e.target.value)}
                        placeholder="https://images.unsplash.com/photo-xxxxx?w=400&q=80"
                        data-testid={`thumbnail-url-input-${index}`}
                      />
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Order (lower numbers appear first)</label>
                      <input
                        type="number"
                        className="admin-form-input"
                        value={formData.order}
                        onChange={(e) => updateFormData(index, 'order', parseInt(e.target.value) || 0)}
                        data-testid={`order-input-${index}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {!editingImage && (
                <button
                  type="button"
                  onClick={handleAddImageSlot}
                  className="admin-button admin-button-secondary"
                  style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  data-testid="add-another-image-btn"
                >
                  <Plus size={20} /> Add Another Image
                </button>
              )}

              <button
                type="submit"
                className="admin-button"
                disabled={submitting || compressingIndex !== null || bulkProgress !== null}
                data-testid="submit-images-btn"
              >
                {submitting
                  ? 'Saving...'
                  : (editingImage ? 'Update Image' : `Add ${formDataList.length} Image${formDataList.length > 1 ? 's' : ''}`)}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
